import React, { useState, useEffect } from "react";
import { X, User, Scale, Check, Eye, EyeOff, Mail, LogOut } from "lucide-react";
import { supabase } from "../supabase";
import type { Session } from "@supabase/supabase-js";
import type { UserProfile, Gender, ActivityLevel } from "../types";
import { getDeviceId } from "../utils/device";

interface AuthModalProps {
  open: boolean;
  session: Session | null;
  onClose: () => void;
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  onSuccess: (session: Session) => void;
  onProfileSaved?: (profile: UserProfile) => void;
  onSignOut?: () => void;
  initialMode?: "signin" | "signup";
  required?: boolean;
  page?: boolean;
  recovery?: boolean;
  onRecoveryComplete?: () => void;
}

type BodyForm = {
  gender: Gender;
  age: string;
  weight_kg: string;
  height_cm: string;
  activity: ActivityLevel;
  goal: "lose" | "maintain" | "gain";
};

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return "authPasswordTooShort";
  if (!/[A-Z]/.test(pw)) return "authPasswordWeak";
  if (!/[a-z]/.test(pw)) return "authPasswordWeak";
  if (!/[0-9]/.test(pw)) return "authPasswordWeak";
  return null;
}

export function AuthModal({ open, session, onClose, t, dir, onSuccess, onProfileSaved, onSignOut,
  initialMode = "signin", required = false, page = false, recovery = false, onRecoveryComplete }: AuthModalProps) {
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("last_email") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stage, setStage] = useState<"auth" | "body" | "forgot" | "reset">("auth");
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [bodyForm, setBodyForm] = useState<BodyForm>({
    gender: "male",
    age: "25",
    weight_kg: "80",
    height_cm: "170",
    activity: "moderate",
    goal: "lose",
  });
  const [savingBody, setSavingBody] = useState(false);
  const [bodyError, setBodyError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(""); setSuccess(""); setPassword(""); setConfirmPassword("");
    if (recovery) {
      setStage("reset");
    } else if (session) {
      setEmail(session.user.email || "");
      setStage("body");
    } else {
      try { setEmail(localStorage.getItem("last_email") || ""); } catch { setEmail(""); }
      setStage("auth");
    }
  }, [open, session, recovery]);

  // Detect password recovery redirect (Supabase sends type=recovery in the hash)
  useEffect(() => {
    if (!open) return;
    const hash = window.location.hash;
    if (hash.includes("type=recovery")) {
      setStage("reset");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (mode === "signup") {
      const pwErr = validatePassword(password);
      if (pwErr) { setError(t(pwErr)); return; }
      if (password !== confirmPassword) { setError(t("authPasswordMismatch")); return; }
      if (!fullName.trim()) { setError(t("authFullName")); return; }
      if (!username.trim() || username.trim().length < 3) { setError(t("authUsernameTooShort")); return; }
    }
    setLoading(true);
    try {
    if (mode === "signin") {
      const { data, error: err } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: rememberMe ? {} : {},
      });
      setLoading(false);
      if (err) {
        const msg = err.message.toLowerCase();
        if (msg.includes("invalid login") || msg.includes("invalid credentials")) setError(t("authWrongPassword"));
        else if (msg.includes("not confirmed") || msg.includes("email not confirmed")) setError(t("authEmailNotConfirmed"));
        else setError(t("authError"));
        return;
      }
      if (data.session) {
        try { localStorage.setItem("last_email", email); } catch {}
        onSuccess(data.session);
        setStage("body");
      }
    } else {
      const { data, error: err } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName.trim(), username: username.trim() } },
      });
      setLoading(false);
      if (err) {
        const msg = err.message.toLowerCase();
        if (msg.includes("already registered") || msg.includes("already been registered") || msg.includes("user already")) setError(t("authEmailExists"));
        else if (msg.includes("invalid email")) setError(t("authInvalidEmail"));
        else setError(t("authError"));
        return;
      }
      try { localStorage.setItem("last_email", email); } catch {}
      if (data.session) {
        // Save full_name to user_profiles
        await supabase.from("user_profiles").upsert({
          device_id: getDeviceId(),
          user_id: data.user!.id,
          full_name: fullName.trim(),
          username: username.trim(),
          email,
        }, { onConflict: "device_id" });
        onSuccess(data.session);
        setStage("body");
      } else {
        setSuccess(t("authSignUpVerify"));
      }
    }
    } catch {
      setError(t("authError"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setSuccess("");
    setResetLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail || email, {
      redirectTo: window.location.origin,
    });
    setResetLoading(false);
    if (err) { setError(t("authError")); return; }
    setSuccess(t("authResetSent"));
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const pwErr = validatePassword(newPassword);
    if (pwErr) { setError(t(pwErr)); return; }
    if (newPassword !== confirmNewPassword) { setError(t("authPasswordMismatch")); return; }
    setResetLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    setResetLoading(false);
    if (err) { setError(t("authResetError")); return; }
    setSuccess(t("authResetSuccess"));
    // Clear the recovery hash
    setTimeout(() => {
      window.history.replaceState(null, "", window.location.pathname);
      setStage("auth");
      setNewPassword(""); setConfirmNewPassword("");
      onRecoveryComplete?.();
    }, 2000);
  };

  const handleResendVerify = async () => {
    setError(""); setSuccess("");
    setResetLoading(true);
    const { error: err } = await supabase.auth.resend({ type: "signup", email: resetEmail || email });
    setResetLoading(false);
    if (err) { setError(t("authError")); return; }
    setSuccess(t("authVerifySent"));
  };

  const saveBody = async (e: React.FormEvent) => {
    e.preventDefault();
    setBodyError("");
    const age = Number(bodyForm.age);
    const weight = Number(bodyForm.weight_kg);
    const height = Number(bodyForm.height_cm);
    if (!age || age < 10 || age > 100) { setBodyError(t("authBodyError")); return; }
    if (!weight || weight < 30 || weight > 300) { setBodyError(t("authBodyError")); return; }
    if (!height || height < 100 || height > 250) { setBodyError(t("authBodyError")); return; }
    setSavingBody(true);
    const deviceId = getDeviceId();
    const payload: UserProfile = {
      device_id: deviceId,
      user_id: session?.user?.id,
      full_name: session?.user?.user_metadata?.full_name || null,
      email: session?.user?.email || null,
      gender: bodyForm.gender,
      age,
      weight_kg: weight,
      height_cm: height,
      activity: bodyForm.activity,
      goal: bodyForm.goal,
    };
    const { error: upErr } = await supabase
      .from("user_profiles")
      .upsert(payload, { onConflict: "device_id" });
    setSavingBody(false);
    if (upErr) { setBodyError(t("authBodySaveError")); return; }
    onProfileSaved?.(payload);
    onClose();
    setStage("auth");
    setPassword("");
  };

  const close = () => {
    if (required) return;
    onClose();
    setStage("auth");
    setSuccess(""); setError("");
  };

  if (!open) return null;

  const isRtl = dir === "rtl";

  return (
    <div className={page ? "min-h-[100dvh] flex items-center justify-center px-4 py-20 bg-gradient-to-br from-[#0A2240] via-[#0E2A50] to-[#050E1F]" : "fixed inset-0 z-50 flex items-end sm:items-center justify-center"} dir={dir}>
      {!page && <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />}
      <div role="dialog" aria-modal={!page} aria-labelledby="auth-heading" className={`relative w-full sm:max-w-sm bg-white dark:bg-slate-900 shadow-2xl border border-stone-100 dark:border-slate-700 overflow-hidden ${page ? "max-w-sm rounded-3xl" : "rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto"}`}>
        {page && <div className="pt-6 px-5 text-center font-bold text-xl text-stone-800 dark:text-slate-100">PURELIFE</div>}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-stone-100 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center">
              {stage === "auth" ? <User className="w-4 h-4 text-blue-500" /> : stage === "forgot" ? <Mail className="w-4 h-4 text-amber-500" /> : stage === "reset" ? <Scale className="w-4 h-4 text-emerald-500" /> : <Scale className="w-4 h-4 text-emerald-500" />}
            </div>
            <h2 id="auth-heading" className="font-bold text-stone-800 dark:text-slate-100 text-base">
              {stage === "auth" ? t(mode === "signup" ? "authCreateAccount" : "signIn") : stage === "forgot" ? t("authForgotTitle") : stage === "reset" ? t("authResetTitle") : t("authBodyTitle")}
            </h2>
          </div>
          {!required && <button onClick={close} aria-label={isRtl ? "إغلاق" : "Close"} className="w-8 h-8 rounded-full bg-stone-100 dark:bg-slate-700 flex items-center justify-center hover:bg-stone-200 dark:hover:bg-slate-600 transition-colors">
            <X className="w-4 h-4 text-stone-500 dark:text-slate-400" />
          </button>}
        </div>

        {stage === "auth" ? (
          <>
            <div className="px-5 pt-4">
              <div className="flex gap-1.5 bg-stone-100 dark:bg-slate-800 rounded-xl p-1">
                <button
                  onClick={() => { setMode("signin"); setError(""); setSuccess(""); }}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === "signin" ? "bg-white dark:bg-slate-700 text-stone-800 dark:text-slate-100 shadow-sm" : "text-stone-500 dark:text-slate-400"}`}
                >{t("signIn")}</button>
                <button
                  onClick={() => { setMode("signup"); setError(""); setSuccess(""); }}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === "signup" ? "bg-white dark:bg-slate-700 text-stone-800 dark:text-slate-100 shadow-sm" : "text-stone-500 dark:text-slate-400"}`}
                >{t("signUp")}</button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
              {mode === "signup" && (
                <div>
                  <label htmlFor="auth-full-name" className="text-sm font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authFullName")}</label>
                  <input
                    id="auth-full-name" autoComplete="name" type="text" required value={fullName} onChange={e => setFullName(e.target.value)}
                    className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-shadow"
                    placeholder={isRtl ? "اسمك الكامل" : "Your full name"}
                  />
                </div>
              )}
              {mode === "signup" && (
                <div>
                  <label htmlFor="auth-username" className="text-sm font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authUsername")}</label>
                  <input
                    id="auth-username" autoComplete="username" type="text" required value={username} onChange={e => setUsername(e.target.value)}
                    className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-shadow"
                    placeholder={isRtl ? "اسم المستخدم" : "username"}
                    minLength={3}
                  />
                </div>
              )}
              <div>
                <label htmlFor="auth-email" className="text-sm font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authEmail")}</label>
                <input
                  id="auth-email" autoComplete="email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-shadow"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label htmlFor="auth-password" className="text-sm font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authPassword")}</label>
                <div className="relative">
                  <input
                    id="auth-password" autoComplete={mode === "signup" ? "new-password" : "current-password"} type={showPassword ? "text" : "password"} required minLength={mode === "signup" ? 8 : 1} value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 pr-11 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-shadow"
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-stone-400 hover:text-stone-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
                    aria-label={showPassword ? t("authHidePassword") : t("authShowPassword")}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {mode === "signup" && (
                <div>
                  <label htmlFor="auth-confirm-password" className="text-sm font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authConfirmPassword")}</label>
                  <input
                    id="auth-confirm-password" autoComplete="new-password" type={showPassword ? "text" : "password"} required minLength={8} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-shadow"
                    placeholder="••••••••"
                  />
                </div>
              )}
              {mode === "signin" && (
                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-stone-300 text-blue-500 focus:ring-blue-400" />
                    <span className="text-xs font-semibold text-stone-600 dark:text-slate-400">{t("authRememberMe")}</span>
                  </label>
                  <button type="button" onClick={() => { setStage("forgot"); setError(""); setSuccess(""); setResetEmail(email); }}
                    className="text-xs font-semibold text-blue-500 hover:text-blue-600 transition-colors">
                    {t("authForgotPassword")}
                  </button>
                </div>
              )}
              {mode === "signup" && (
                <p className="text-xs text-stone-500 dark:text-slate-400">{isRtl ? "8 أحرف على الأقل، أحرف كبيرة وصغيرة وأرقام" : "Min 8 chars, uppercase, lowercase & numbers"}</p>
              )}
              {error && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
              {success && (
                <div className="space-y-2">
                  <p className="text-xs text-green-600 bg-green-50 dark:bg-green-500/10 rounded-lg px-3 py-2">{success}</p>
                  {success === t("authSignUpVerify") && (
                    <button type="button" onClick={handleResendVerify} disabled={resetLoading}
                      className="text-xs font-semibold text-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50">
                      {resetLoading ? "..." : t("authResendVerify")}
                    </button>
                  )}
                </div>
              )}
              <button
                type="submit" disabled={loading}
                className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-bold text-sm transition-colors shadow-md"
              >
                {loading ? "..." : mode === "signin" ? t("signIn") : t("authCreateAccount")}
              </button>
              <p className="text-center text-xs text-stone-400 dark:text-slate-500">
                {mode === "signin" ? t("authNoAccount") : t("authHaveAccount")}{" "}
                <button type="button" onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(""); setSuccess(""); }}
                  className="font-bold text-blue-500 hover:text-blue-600 transition-colors">
                  {mode === "signin" ? t("signUp") : t("signIn")}
                </button>
              </p>
            </form>
          </>
        ) : stage === "forgot" ? (
          <form onSubmit={handleResetPassword} className="px-5 py-4 space-y-3">
            <p className="text-xs text-stone-500 dark:text-slate-400">{t("authForgotSubtitle")}</p>
            <div>
              <label className="text-xs font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authEmail")}</label>
              <input
                type="email" required value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-shadow"
                placeholder="you@example.com"
              />
            </div>
            {error && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
            {success && <p className="text-xs text-green-600 bg-green-50 dark:bg-green-500/10 rounded-lg px-3 py-2">{success}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setStage("auth"); setError(""); setSuccess(""); }}
                className="flex-1 py-3 rounded-xl border border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300 text-sm font-medium transition-colors">
                {t("wlBack")}
              </button>
              <button type="submit" disabled={resetLoading}
                className="flex-[2] py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm transition-all shadow-md disabled:opacity-60">
                {resetLoading ? "..." : t("authSendResetLink")}
              </button>
            </div>
          </form>
        ) : stage === "reset" ? (
          <form onSubmit={handleUpdatePassword} className="px-5 py-4 space-y-3">
            <p className="text-xs text-stone-500 dark:text-slate-400">{t("authResetSubtitle")}</p>
            <div>
              <label className="text-xs font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authNewPassword")}</label>
              <input
                type="password" required minLength={8} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-shadow"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600 dark:text-slate-400 block mb-1">{t("authConfirmPassword")}</label>
              <input
                type="password" required minLength={8} value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)}
                className="w-full border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-stone-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-shadow"
                placeholder="••••••••"
              />
            </div>
            {error && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
            {success && <p className="text-xs text-green-600 bg-green-50 dark:bg-green-500/10 rounded-lg px-3 py-2">{success}</p>}
            <button type="submit" disabled={resetLoading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold text-sm transition-all shadow-md disabled:opacity-60">
              {resetLoading ? "..." : t("authChangePassword")}
            </button>
          </form>
        ) : (
          <form onSubmit={saveBody} className="px-5 py-4 space-y-4">
            <p className="text-xs text-stone-500 dark:text-slate-400">{t("authBodySubtitle")}</p>

            <div>
              <label className="text-xs font-semibold text-stone-600 dark:text-slate-400 block mb-1.5">{isRtl ? "الجنس" : "Gender"}</label>
              <div className="flex gap-3">
                {(["male", "female"] as const).map(g => (
                  <button key={g} type="button" onClick={() => setBodyForm(p => ({ ...p, gender: g }))}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${bodyForm.gender === g ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "border-stone-200 dark:border-slate-600 text-stone-500 dark:text-slate-400"}`}>
                    {isRtl ? (g === "male" ? "ذكر" : "أنثى") : g === "male" ? "Male" : "Female"}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {([
                { key: "age", label: isRtl ? "العمر" : "Age", min: 10, max: 100 },
                { key: "weight_kg", label: isRtl ? "الوزن (كجم)" : "Weight (kg)", min: 30, max: 300 },
                { key: "height_cm", label: isRtl ? "الطول (سم)" : "Height (cm)", min: 100, max: 250 },
              ] as const).map(({ key, label, min, max }) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs text-stone-500 dark:text-slate-400">{label}</label>
                  <input type="number" min={min} max={max} required value={bodyForm[key]}
                    onChange={e => setBodyForm(p => ({ ...p, [key]: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 dark:border-slate-600 bg-stone-50 dark:bg-slate-700 text-stone-800 dark:text-slate-100 px-3 py-2.5 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              ))}
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 dark:text-slate-400 block mb-1.5">{isRtl ? "مستوى النشاط" : "Activity level"}</label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ["sedentary", isRtl ? "خامل" : "Sedentary"],
                  ["light", isRtl ? "خفيف" : "Light"],
                  ["moderate", isRtl ? "معتدل" : "Moderate"],
                  ["active", isRtl ? "نشيط" : "Active"],
                ] as const).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setBodyForm(p => ({ ...p, activity: k }))}
                    className={`py-2 rounded-xl text-xs font-semibold border-2 transition-all ${bodyForm.activity === k ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "border-stone-200 dark:border-slate-600 text-stone-500 dark:text-slate-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 dark:text-slate-400 block mb-1.5">{isRtl ? "الهدف" : "Goal"}</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  ["lose", isRtl ? "إنقاص" : "Lose"],
                  ["maintain", isRtl ? "ثبات" : "Maintain"],
                  ["gain", isRtl ? "زيادة" : "Gain"],
                ] as const).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setBodyForm(p => ({ ...p, goal: k as BodyForm["goal"] }))}
                    className={`py-2 rounded-xl text-xs font-semibold border-2 transition-all ${bodyForm.goal === k ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "border-stone-200 dark:border-slate-600 text-stone-500 dark:text-slate-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {bodyError && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2">{bodyError}</p>}

            <div className="flex gap-2">
              <button type="button" onClick={() => { setStage("auth"); }}
                className="flex-1 py-3 rounded-xl border border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300 text-sm font-medium transition-colors">
                {t("wlBack")}
              </button>
              <button type="submit" disabled={savingBody}
                className="flex-[2] py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md disabled:opacity-60">
                {savingBody ? "..." : (<><Check className="w-4 h-4" /> {t("authBodySave")}</>)}
              </button>
            </div>

            {onSignOut && (
              <button type="button" onClick={() => { onSignOut(); }}
                className="w-full py-2.5 rounded-xl border-2 border-red-300 dark:border-red-500/40 text-red-600 dark:text-red-400 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all">
                <LogOut className="w-4 h-4" /> {t("signOutThisDevice")}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
